/**
 * Creates a table from the response of a query using listData.json service
 */

import {
    Component,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    Signal,
    ChangeDetectorRef,
    inject,
    input,
    viewChild,
    untracked,
    computed,
    effect,
    output,
    model,
} from "@angular/core";
import { ListData } from "../../pojo/ListData";
import { s25Resource } from "../../util/s25.resource";
import { S25RadioComponent } from "../s25-radio/s25.radio.component";
import { Table } from "../s25-table/Table";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25TableModule } from "../s25-table/s25.table.module";
import { Bind } from "../../decorators/bind.decorator";
import { ButtonClassSuffix, S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { ListDataParams2, ListService } from "../../services/list.service";
import { S25Util } from "../../util/s25-util";
import { CommonModule, NgTemplateOutlet } from "@angular/common";
import { UserprefService } from "../../services/userpref.service";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { AddRemoveUtil } from "../../standalone/more-actions/edit/add.remove.util";
import { ListDataEnhancer } from "./list-data-enhancer";

export type { ListDataEnhancer, ListDataEnhanceRowsContext } from "./list-data-enhancer";

@TypeManagerDecorator("s25-ng-list-data")
@Component({
    selector: "s25-ng-list-data",
    template: `
        @if (showActions() === "top" || showActions() === "both") {
            <ng-container [ngTemplateOutlet]="actionsTemplate"></ng-container>
        }
        @if (tableConfig() && !loading()) {
            <s25-ng-table
                [columnSortable]="true"
                [dataSource]="tableConfig()!"
                [unlimitedWidth]="true"
                [pivotThreshold]="0"
                [hasTotalRowCount]="false"
                [hasSelect]="hasSelect()"
                [hasSelectAll]="hasSelect()"
                [(selected)]="selectedItems"
                (selectedChange)="handleSelectedChange($event)"
            ></s25-ng-table>
        }
        <!-- TODO: don't show both if the table is not visible -->
        @if (showActions() === "bottom" || showActions() === "both") {
            <ng-container [ngTemplateOutlet]="actionsTemplate"></ng-container>
        }
        <ng-template #actionsTemplate>
            @if (!tableComponent()?.loading() && actions()?.length) {
                <div class="actions-tray">
                    @for (action of actions(); track action.text()) {
                        @if (action.subText()) {
                            <div class="action with-subtext">
                                <ng-container
                                    [ngTemplateOutlet]="actionButton"
                                    [ngTemplateOutletContext]="{ $implicit: action }"
                                />
                                <span class="ng25LiveGrey">{{ action.subText() }}</span>
                            </div>
                        } @else {
                            <div class="action">
                                <ng-container
                                    [ngTemplateOutlet]="actionButton"
                                    [ngTemplateOutletContext]="{ $implicit: action }"
                                />
                            </div>
                        }
                    }
                </div>
            }
        </ng-template>
        <ng-template #actionButton let-action>
            <s25-ng-button
                [type]="action.type"
                [disabled]="action.activateOnChecked && selectedItems()?.size === 0"
                [buttonText]="action.text()"
                [title]="action.subText()"
                [buttonClass]="action.classList || ''"
                (clicked)="action.action(tableComponent())"
            ></s25-ng-button>
        </ng-template>
    `,
    styles: `
        .actions-tray {
            display: flex;
            flex-wrap: wrap;
            gap: 0.75em 1.5em;
            justify-content: center;
        }
        .action.with-subtext {
            display: flex;
            flex-direction: column;
            align-items: center;
        }
        s25-ng-button ~ s25-ng-button {
            margin-top: 0.75em;
        }
    `,
    standalone: true,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25TableModule, S25ButtonComponent, CommonModule, NgTemplateOutlet],
})
export class S25ListDataComponent<TPrimary = AddRemoveUtil.ContextId> {
    readonly query = input<ListDataParams2>();
    readonly hasSelect = input<boolean>(false);
    readonly tableConfigInput = input<Table.DataSource>();
    readonly actions = input<Action[]>([]);
    readonly showActions = input<"top" | "bottom" | "both">("both");
    readonly keepSelectionsOnPageChange = input<boolean>(false);
    readonly selectedItems = model(new Set<Table.Row["id"]>());
    /** Parent-owned specialization for domain-specific list behavior. */
    readonly enhancer = input<ListDataEnhancer>();

    readonly selectedChange = output<Set<Table.Row["id"]>>();
    readonly primaryChange = output<TPrimary>();

    readonly tableComponent = viewChild(S25TableComponent);

    readonly cd = inject(ChangeDetectorRef);

    readonly dateFormat = s25Resource(() => UserprefService.getS25Dateformat());
    readonly timeFormat = s25Resource(() => UserprefService.getS25Timeformat());
    readonly dateTimeFormat = s25Resource(() => UserprefService.getS25DateTimeformat());

    readonly tableConfig = computed((): Table.DataSource | undefined => {
        if (!this.query()) {
            return undefined;
        } else {
            return {
                ...this.tableConfigInput(),
                type: this.tableConfigInput()?.type ?? "paginated",
                keepSelectionsOnPageChange: this.keepSelectionsOnPageChange(),
                dataSource: this.getDataPaginated.bind(this),
                startPage: this.query()!.page || 0,
                defaultPageSize: this.query()!.itemsPerPage ?? 25,
                pageSizes: [25, 50, 100],
                columns: this.tableConfigInput()?.columns ?? [],
                customRowCount: this.getRowCount,
                hasMorePages: () => false, //TODO: implement has more pages if when needed
            };
        }
    });
    readonly loading = computed(
        () =>
            this.dateFormat.resource.isLoading() ||
            this.timeFormat.resource.isLoading() ||
            this.dateTimeFormat.resource.isLoading(),
    );

    @Bind
    getRowCount(counts: Table.RowCounts) {
        const type = S25Util.firstCharToUpper(this.query()!.itemType) + (counts.total === 1 ? "" : "s");
        return `${counts.total} Matching ${type}`;
    }

    constructor() {
        effect(() => {
            // when the tableConfig changes, and the table is not already loading
            if (this.tableConfig() && untracked(() => !this.tableComponent()?.loading())) {
                return this.tableComponent()?.refresh(false, true, this.keepSelectionsOnPageChange());
            }
        });

        effect(() => {
            const token = this.enhancer()?.refreshToken?.();
            if (token === undefined) {
                return;
            }
            untracked(() => {
                const table = this.tableComponent();
                if (table && !table.loading()) {
                    void table.refresh(false, false, true);
                }
            });
        });
    }

    setColumnsFromListData(listResp: ListData.Wrapper) {
        let columns: Table.Column[] = listResp.cols.map((column) => ({
            id: column.prefname,
            header: column.name,
            sortable: column.sortable === 1,
            type: !!column.isDate ? "date" : undefined,
        }));

        columns = this.enhancer()?.enhanceColumns?.(columns) ?? columns;
        this.tableComponent()?.setColumns(columns);
    }

    async getDataFromService(query: Table.PaginatedQuery): Promise<ListData.Wrapper> {
        const listQuery: ListDataParams2 = {
            itemType: this.query()!.itemType,
            query: this.query()!.query,
            page: query.page + 1, //List service is 1 based
            sort: { column: query.sortColumn.id, order: query.sortColumn.order || "asc" },
            useCache: true,
            cacheId: Number(query.cacheId) || 0,
            comptype: this.query()!.comptype,
            itemsPerPage: query.pageSize || this.query()!.itemsPerPage,
        };
        return await ListService.getData2(listQuery);
    }

    @Bind
    async getDataPaginated(query: Table.PaginatedQuery): Promise<Table.DataSourceResponse> {
        const [resp, err] = await S25Util.Maybe(this.getDataFromService(query));
        const retVal = this.listToTable(resp);
        this.setColumnsFromListData(resp);
        retVal.totalRows = resp?.rows[0]?.count || 1;
        return retVal;
    }

    handleSelectedChange(selected: Set<Table.Row["id"]>): void {
        this.selectedChange.emit(selected);
    }

    clearSelections(): void {
        this.tableComponent()?.clearSelections();
    }

    listToTable(listResp: ListData.Wrapper): Table.DataSourceResponse {
        // is this a jenky way to find the name column?
        const nameColIndex = listResp.cols.findIndex((col) => col.isPermanent);

        let rows: Table.Row<TPrimary>[] = listResp.rows.map((rowWrapper) => ({
            id: rowWrapper.contextId,
            name: <string>rowWrapper.row[nameColIndex],
            cells: rowWrapper.row.reduce(
                (acc, rowColValue, index) => {
                    const column = listResp.cols[index];
                    if (!column) {
                        //If the column is not defined by table drop it
                        return acc;
                    }
                    if (typeof rowColValue === "object") {
                        if (
                            column.prefname === ListService.IsPrimaryCol &&
                            // Do we need to check both? Probably not?
                            rowColValue.templateType === ListData.TemplateType.IsPrimary
                        ) {
                            if (this.hasSelect()) {
                                return acc; // skip primary column if selection is enabled
                            }
                            const isPrimary = rowColValue.value === "primary";
                            acc[ListService.IsPrimaryCol] = {
                                component: S25RadioComponent<TPrimary>,
                                inputs: {
                                    value: isPrimary ? rowWrapper.contextId : null,
                                    radioValue: rowWrapper.contextId,
                                    // TODO: make this unique across multiple tables on the same page
                                    name: ListService.IsPrimaryCol,
                                },
                                align: "center",
                                outputs: {
                                    valueChange: (eventId: TPrimary) => this.handlePrimaryChange(eventId),
                                },
                                textValue: isPrimary ? "Primary" : "",
                            };
                        } else {
                            console.warn("Unexpected column value", rowColValue, "for column", column.prefname);
                        }
                    } else if (column.isDate) {
                        acc[column.prefname] = {
                            text: S25Datefilter.transform(S25Util.date.parse(rowColValue), this.dateFormat.value()!),
                        };
                    } else if (column.isTime) {
                        acc[column.prefname] = {
                            text: S25Datefilter.transform(S25Util.date.parse(rowColValue), this.timeFormat.value()!),
                        };
                    } else if (column.isDateTime) {
                        acc[column.prefname] = {
                            text: S25Datefilter.transform(
                                S25Util.date.parse(rowColValue),
                                this.dateTimeFormat.value()!,
                            ),
                        };
                    } else {
                        acc[column.prefname] = { text: rowColValue };
                    }

                    return acc;
                },
                {} as Record<string, Table.Cell>,
            ),
        }));

        rows =
            this.enhancer()?.enhanceRows?.(rows, listResp, {
                dateFormat: this.dateFormat.value(),
            }) ?? rows;

        return { rows };
    }

    handlePrimaryChange(eventId: TPrimary): void {
        this.primaryChange.emit(eventId);
    }
}

export type Action = {
    type: ButtonClassSuffix;
    // these should not be signals
    text: Signal<string>;
    subText: Signal<string>;
    activateOnChecked: boolean;
    action: (table: S25TableComponent) => Promise<void>;
    classList?: string;
};
